import { nowInTokyo, parseYmdInTokyo } from "../../workLogTimezone";
import type {
  DisabledTimeData,
  FilterableColumnKey,
  OriginalCellData,
  TrackingAgRow,
} from "./type";

export const getWorkerLogtimeDisplayTime = (
  value:
    | { display_time?: string | null; time?: string | null }
    | null
    | undefined,
): string => value?.display_time ?? value?.time ?? "";

export const getOriginalCellData = (
  rowData: TrackingAgRow | undefined,
  field: string,
): OriginalCellData | null | undefined =>
  rowData?.[`${field}Data`] as OriginalCellData | null | undefined;

export const getTrackingDisabledTimeData = (
  rowData: TrackingAgRow,
  field: string,
  correctedTimeFields: string[],
): DisabledTimeData => {
  const createRange = (start: number, end: number) =>
    Array.from(
      { length: Math.max(end - start, 0) },
      (_, index) => index + start,
    );
  const toMinutes = (value: unknown): number | undefined => {
    const match = String(value ?? "")
      .trim()
      .match(/^(\d{1,2}):([0-5]\d)$/);
    if (!match) return undefined;
    return Number(match[1]) * 60 + Number(match[2]);
  };
  const fieldIndex = correctedTimeFields.indexOf(field);
  const findAdjacentTime = (step: -1 | 1) => {
    for (
      let index = fieldIndex + step;
      index >= 0 && index < correctedTimeFields.length;
      index += step
    ) {
      const minutes = toMinutes(rowData[correctedTimeFields[index]]);
      if (minutes != null) return minutes;
    }
    return undefined;
  };

  const previousTime = findAdjacentTime(-1);
  const nextTime = findAdjacentTime(1);
  const minimumTime = previousTime != null ? previousTime : 0;
  let maximumTime = nextTime != null ? nextTime : 99 * 60 + 59;
  const now = nowInTokyo();
  const today = now.format("YYYY-MM-DD");
  const rowDate = rowData.date;

  if (rowDate > today) {
    maximumTime = -1;
  } else if (rowDate === today) {
    maximumTime = Math.min(maximumTime, now.hour() * 60 + now.minute());
  } else {
    // Extended hours are elapsed time from midnight of the work date.
    // For example, yesterday's 27:00 is today's 03:00.
    const workDateStart = parseYmdInTokyo(rowDate);
    if (workDateStart) {
      maximumTime = Math.min(maximumTime, now.diff(workDateStart, "minute"));
    }
  }

  return {
    disabledHours: () =>
      createRange(0, 100).filter((hour) => {
        const firstMinute = hour * 60;
        const lastMinute = firstMinute + 59;
        return lastMinute < minimumTime || firstMinute > maximumTime;
      }),
    disabledMinutes: (selectedHour: number) =>
      createRange(0, 60).filter((minute) => {
        const selectedTime = selectedHour * 60 + minute;
        return selectedTime < minimumTime || selectedTime > maximumTime;
      }),
  };
};

export const getV2FilterFieldFromColumn = (
  columnId: FilterableColumnKey,
): string | undefined => {
  const fixedFields: Record<string, string> = {
    employeeName: "worker_names",
    date: "dates",
    startAt: "punch_check_in",
    endAt: "punch_check_out",
    correctedCheckIn: "corrected_check_in",
    correctedCheckOut: "corrected_check_out",
    memo: "notes",
    workingTime: "working_hours",
    breakTime: "break_hours",
    status: "logtime_statuses",
    creator: "updater_names",
    createdAt: "updated_at",
  };
  if (fixedFields[columnId]) return fixedFields[columnId];

  const dynamicField = columnId.match(
    /^(punch|corrected)Break(Start|End)(\d+)$/,
  );
  if (!dynamicField) return undefined;

  const [, type, boundary, zeroBasedIndex] = dynamicField;
  return `${type}_break_${
    Number(zeroBasedIndex) + 1
  }_${boundary.toLowerCase()}`;
};

export const getColumnFromV2FilterField = (
  apiField: string,
): FilterableColumnKey | undefined => {
  const fixedColumns: Record<string, FilterableColumnKey> = {
    worker_names: "employeeName",
    dates: "date",
    punch_check_in: "startAt",
    punch_check_out: "endAt",
    corrected_check_in: "correctedCheckIn",
    corrected_check_out: "correctedCheckOut",
    notes: "memo",
    working_hours: "workingTime",
    break_hours: "breakTime",
    logtime_statuses: "status",
    updater_names: "creator",
    updated_at: "createdAt",
  };
  if (fixedColumns[apiField]) return fixedColumns[apiField];

  const dynamicField = apiField.match(
    /^(punch|corrected)_break_(\d+)_(start|end)$/,
  );
  if (!dynamicField) return undefined;

  const [, type, oneBasedIndex, boundary] = dynamicField;
  const boundaryName = boundary === "start" ? "Start" : "End";
  return `${type}Break${boundaryName}${Number(oneBasedIndex) - 1}`;
};
